// Survey Validation and Auto-Focus Utilities
// ==========================================

/**
 * Scrolls to and focuses the first invalid field with enhanced visual feedback
 * @param {string} fieldIdentifier - CSS selector or field identifier
 * @param {Object} options - Configuration options
 */
export const scrollToAndFocusField = (fieldIdentifier, options = {}) => {
  // ... (function body remains the same)
};

/**
 * Enhanced validation function that returns detailed field information
 * @param {Object} formData - Form data object
 * @param {number} step - Current step number
 * @param {string} study - Study type (elbow, shoulder arthroscopy, etc.)
 * @returns {Object} Validation result with field details
 */
export const validateFormWithFieldDetails = (formData, step, study) => {
  const validationResult = {
    isValid: true,
    firstInvalidField: null,
    invalidFields: [],
    errorMessage: '',
    fieldErrors: {},
  };

  try {
    switch (step) {
      case 1: { // VAS Score
        if (!formData.vasScore || formData.vasScore === '' || parseFloat(formData.vasScore) < 0 || parseFloat(formData.vasScore) > 10) {
          validationResult.isValid = false;
          const fieldPath = 'vasScore';
          const message = 'Gelieve een pijnwaarde tussen 0 en 10 te selecteren';
          validationResult.firstInvalidField = '[name="vasScore"]';
          validationResult.invalidFields.push({ field: fieldPath, selector: '[name="vasScore"]', type: 'range', message });
          validationResult.fieldErrors[fieldPath] = message;
          validationResult.errorMessage = message;
        }
        break;
      }

      case 2: { // ASES or StandardEarlyPostOp
        if (formData.ases) {
          const maxQuestions = (study === "elbow") ? 18 : 17;
          for (let i = 1; i <= maxQuestions; i++) {
            const fieldName = `asesQuestion${i}`;
            const fieldPath = `ases.${fieldName}`;
            const fieldValue = formData.ases?.[fieldName];
            if (!fieldValue || fieldValue === '') {
              validationResult.isValid = false;
              const selector = `[name="${fieldName}"]`;
              const message = `Vraag ${i} is verplicht`;
              if (!validationResult.firstInvalidField) validationResult.firstInvalidField = selector;
              validationResult.invalidFields.push({ field: fieldName, selector, type: 'select', message });
              validationResult.fieldErrors[fieldPath] = message;
            }
          }
        }
        if (formData.sepostop) {
          for (let i = 1; i <= 3; i++) {
            const fieldName = `sepostopQuestion${i}`;
            const fieldPath = `sepostop.${fieldName}`;
            const fieldValue = formData.sepostop?.[fieldName];
            if (!fieldValue || fieldValue === '') {
              validationResult.isValid = false;
              const selector = `[name="${fieldName}"]`;
              const message = `Vraag ${i} is verplicht`;
              if (!validationResult.firstInvalidField) validationResult.firstInvalidField = selector;
              validationResult.invalidFields.push({ field: fieldName, selector, type: 'select', message });
              validationResult.fieldErrors[fieldPath] = message;
            }
          }
        }
        break;
      }

      case 3: { // SANE Score
        if (!formData.saneScore || formData.saneScore === '' || parseInt(formData.saneScore) < 0 || parseInt(formData.saneScore) > 100) {
          validationResult.isValid = false;
          const fieldPath = 'saneScore';
          const message = 'Gelieve een waarde tussen 0 en 100 te selecteren';
          validationResult.firstInvalidField = '[name="saneScore"]';
          validationResult.invalidFields.push({ field: fieldPath, selector: '[name="saneScore"]', type: 'range', message });
          validationResult.fieldErrors[fieldPath] = message;
          validationResult.errorMessage = message;
        }
        break;
      }

      case 4: { // Standard Pre-Op or VR12
        if (formData.spreop) {
          for (let i = 1; i <= 4; i++) {
            const fieldName = `spreopQuestion${i}`;
            const fieldPath = `spreop.${fieldName}`;
            const fieldValue = formData.spreop?.[fieldName];
            if (!fieldValue || fieldValue === '') {
              validationResult.isValid = false;
              const selector = `[name="${fieldName}"]`;
              const message = `Vraag ${i} is verplicht`;
              if (!validationResult.firstInvalidField) validationResult.firstInvalidField = selector;
              validationResult.invalidFields.push({ field: fieldName, selector, type: 'select', message });
              validationResult.fieldErrors[fieldPath] = message;
            }
          }
        } else if (formData.vr12) {
          for (let i = 1; i <= 14; i++) {
            const fieldName = `vr12Question${i}`;
            const fieldPath = `vr12.${fieldName}`;
            const fieldValue = formData.vr12?.[fieldName];
            if (!fieldValue || fieldValue === '') {
              validationResult.isValid = false;
              const selector = `[name="${fieldName}"]`;
              const message = `Vraag ${i} is verplicht`;
              if (!validationResult.firstInvalidField) validationResult.firstInvalidField = selector;
              validationResult.invalidFields.push({ field: fieldName, selector, type: 'select', message });
              validationResult.fieldErrors[fieldPath] = message;
            }
          }
        }
        break;
      }

      case 5: { // VR12 or StandardLatePostOp
        if (formData.slpostop) {
          for (let i = 1; i <= 4; i++) {
            const fieldName = `slpostopQuestion${i}`;
            const fieldPath = `slpostop.${fieldName}`;
            const fieldValue = formData.slpostop?.[fieldName];
            if (!fieldValue || fieldValue === '') {
              validationResult.isValid = false;
              const selector = `[name="${fieldName}"]`;
              const message = `Vraag ${i} is verplicht`;
              if (!validationResult.firstInvalidField) validationResult.firstInvalidField = selector;
              validationResult.invalidFields.push({ field: fieldName, selector, type: 'select', message });
              validationResult.fieldErrors[fieldPath] = message;
            }
          }
        } else if (formData.vr12) {
          for (let i = 1; i <= 14; i++) {
            const fieldName = `vr12Question${i}`;
            const fieldPath = `vr12.${fieldName}`;
            const fieldValue = formData.vr12?.[fieldName];
            if (!fieldValue || fieldValue === '') {
              validationResult.isValid = false;
              const selector = `[name="${fieldName}"]`;
              const message = `Vraag ${i} is verplicht`;
              if (!validationResult.firstInvalidField) validationResult.firstInvalidField = selector;
              validationResult.invalidFields.push({ field: fieldName, selector, type: 'select', message });
              validationResult.fieldErrors[fieldPath] = message;
            }
          }
        }
        break;
      }

      default:
        validationResult.isValid = true;
        break;
    }

    if (!validationResult.isValid && validationResult.invalidFields.length > 0 && !validationResult.errorMessage) {
      validationResult.errorMessage = `Er zijn ${validationResult.invalidFields.length} verplichte velden niet ingevuld`;
    }

  } catch (error) {
    console.error('Validation error:', error);
    validationResult.isValid = false;
    validationResult.errorMessage = 'Er is een fout opgetreden bij de validatie';
  }

  return validationResult;
};

const clearFieldValidationStates = () => {
    const invalidFields = document.querySelectorAll('.invalid');
    invalidFields.forEach(field => {
        field.classList.remove('invalid');
    });
};

const highlightInvalidFields = (invalidFields) => {
    invalidFields.forEach(error => {
        const field = document.querySelector(error.selector);
        if (field) {
            field.classList.add('invalid');
        }
    });
};

const focusFirstInvalidField = (validationResult) => {
    if (validationResult.firstInvalidField) {
        const field = document.querySelector(validationResult.firstInvalidField);
        if (field) {
            field.scrollIntoView({ behavior: 'smooth', block: 'center' });
            field.focus();
        }
    }
};

// ... (rest of the file remains the same)

export const validateAndFocusForm = (formData, step, study, options = {}) => {
  const {
    clearPreviousErrors = true,
    showVisualFeedback = true,
    scrollToFirst = true
  } = options;

  if (clearPreviousErrors) {
    clearFieldValidationStates();
  }

  const validationResult = validateFormWithFieldDetails(formData, step, study);

  if (!validationResult.isValid && showVisualFeedback) {
    highlightInvalidFields(validationResult.invalidFields);

    if (scrollToFirst) {
      focusFirstInvalidField(validationResult);
    }
  }

  return validationResult;
};
